import React, { useEffect, Suspense, useMemo } from 'react';
import {
  Route,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useParams,
  useLocation,
  useMatches,
} from 'react-router-dom';
import { withRouter } from '@lib/router';
import { TopProgressBar, RouteFallback, lazy } from '@components/common/routeLoading';
import { requiresAuth, requiresMfaSession } from '@auth/appRouter/requiresAuth';
import { disableCognito } from '@config';
import useStores from '@stores/useStores';
import { observer } from 'mobx-react';
import { RESOURCE_TYPES, SESSION_STORAGE_KEYS } from '@components/common/constants';
import { RudderCategory } from '@components/common/types';
import { apiAuthCaller } from '@services/apiCaller';
import { Path } from '@components/routes/constants';
import { McpAuthorizationPage } from '@components/mcpAuthorizationPage';

import { OAUTH_POST_MESSAGE_TYPE, OAUTH_CHANNEL_NAME } from '../../constants';
import MetaTag from './metaTag';
import { Toast } from './toast';
import { RouteErrorBoundary } from './routeErrorBoundary';
import { useFeatureFlagObserver } from '@hooks/useFeatureFlagObserver';
import { usePageTracking } from '@hooks/usePageTracking';
import { FeatureFlagKeys } from '@lib/experiment';

const ForgotPasswordPage = lazy(
  () => import(/* webpackChunkName: "auth.forgotPassword" */ '@auth/forgotPassword'),
);
const SSOPage = lazy(() => import(/* webpackChunkName: "auth.sso" */ '@auth/sso'));
const ESignupPage = lazy(
  () => import(/* webpackChunkName: "auth.esignup" */ '@auth/signup/esignup'),
);
const EsignupV2Page = lazy(() =>
  import(/* webpackChunkName: "auth.esignupV2" */ '@authV2/esignup').then((m) => ({
    default: m.EsignupV2,
  })),
);
const Home = lazy(() => import(/* webpackChunkName: "home" */ '@components/home/home'));
const AcceptInvite = lazy(
  () => import(/* webpackChunkName: "auth.acceptInvite" */ '@auth/acceptInvite'),
);
const LoginPage = lazy(() => import(/* webpackChunkName: "auth.login" */ '@auth/login/login'));
const BlendoSignupPage = lazy(
  () => import(/* webpackChunkName: "auth.blendoSignup" */ '@auth/blendo/blendoSignup'),
);
const MfaVerify = lazy(
  () => import(/* webpackChunkName: "auth.mfaVerify" */ '@auth/mfa/mfaVerify'),
);
const AddMfa = lazy(() => import(/* webpackChunkName: "auth.addMfa" */ '@auth/mfa/addMfa'));
const AddMfaLater = lazy(
  () => import(/* webpackChunkName: "auth.addMfaLater" */ '@auth/mfa/addMfaLater'),
);
const AddMfaSuccess = lazy(
  () => import(/* webpackChunkName: "auth.addMfaSuccess" */ '@auth/mfa/addMfaSuccess'),
);
const AddPhonePage = lazy(
  () => import(/* webpackChunkName: "auth.addPhonePage" */ '@auth/addPhonePage'),
);
const TotpVerify = lazy(() =>
  import(/* webpackChunkName: "auth.totpVerify" */ '@auth/mfa/totpVerify').then((m) => ({
    default: m.MfaVerifyPage,
  })),
);
const TotpSetup = lazy(() =>
  import(/* webpackChunkName: "auth.totpSetup" */ '@auth/mfa/totpSetup').then((m) => ({
    default: m.TotpSetupPage,
  })),
);
const Signup = lazy(() => import(/* webpackChunkName: "auth.signup" */ '@auth/signup'));
const SetupAccountV2Flow = lazy(() =>
  import(/* webpackChunkName: "auth.setupAccountV2" */ '@authV2/setupAccountV2').then((m) => ({
    default: m.SetupAccountV2,
  })),
);
const SetupAccountOriginal = lazy(() =>
  import(
    /* webpackChunkName: "auth.setupAccountOriginal" */ '@auth/setupAccount/setupAccount'
  ).then((m) => ({
    default: m.SetupAccountOriginal,
  })),
);
const Security = lazy(() => import(/* webpackChunkName: "security" */ '@components/security'));
const WaitingAuthentication = lazy(
  () => import(/* webpackChunkName: "auth.WaitingAuthentication" */ './WaitingAuthentication'),
);
const SelectOrganization = lazy(
  () => import(/* webpackChunkName: "organizations" */ '@components/organizations'),
);
const UserSuspendedInOrganization = lazy(() =>
  import(
    /* webpackChunkName: "organizations.suspended" */ '@components/organizations/suspended'
  ).then((m) => ({
    default: m.UserSuspendedInOrganization,
  })),
);

// Route guards are stable module-level components so v6 `element={<X />}` does not
// remount them on every render of AppRouter.
const GuardedAddMfa = requiresMfaSession(AddMfa);
const GuardedAddMfaLater = requiresAuth(AddMfaLater);
const GuardedAddMfaSuccess = requiresMfaSession(AddMfaSuccess);
const GuardedAddPhone = requiresMfaSession(AddPhonePage);
const GuardedVerifyPhone = requiresMfaSession(MfaVerify);
const GuardedVerifyTotp = requiresMfaSession(TotpVerify);
const GuardedSetupTotp = requiresMfaSession(TotpSetup);
const GuardedUserSuspended = requiresAuth(UserSuspendedInOrganization);
const GuardedSelectOrganization = requiresAuth(SelectOrganization);
const GuardedHome = requiresAuth(Home);

// Legacy page components that read router props (history/location/match) via the old
// `component={}` injection; withRouter supplies those under v6's `element={}` model.
const ForgotPassword = withRouter(ForgotPasswordPage) as unknown as React.FC;
const EsignupV1Page = withRouter(ESignupPage) as unknown as React.FC;
const EsignupV2Wrapped = withRouter(EsignupV2Page) as unknown as React.FC;
const SignupComponent = disableCognito
  ? EsignupV1Page
  : (withRouter(Signup) as unknown as React.FC);
const GuardedSetupAccountV1 = requiresAuth(SetupAccountOriginal);
const GuardedSetupAccountV2 = requiresAuth(SetupAccountV2Flow);
const enableMFA = !disableCognito;

/**
 * Route-level switches for the PLG onboarding v2 flag. The flag resolves
 * asynchronously after mount, so the branching must live INSIDE a stable route
 * element: the router (and its history listeners) is created exactly once,
 * while the rendered page swaps when the flag flips.
 */
const EsignupSwitch: React.FC = () => {
  const isPlgOnboardingV2Enabled = useFeatureFlagObserver(FeatureFlagKeys.enablePlgOnboardingV2);
  return isPlgOnboardingV2Enabled ? <EsignupV2Wrapped /> : <EsignupV1Page />;
};

const SetupAccountSwitch: React.FC = () => {
  const isPlgOnboardingV2Enabled = useFeatureFlagObserver(FeatureFlagKeys.enablePlgOnboardingV2);
  return isPlgOnboardingV2Enabled ? <GuardedSetupAccountV2 /> : <GuardedSetupAccountV1 />;
};

const OauthRedirect: React.FC = () => {
  useEffect(() => {
    const { search } = window.location;
    const params = new URLSearchParams(search);
    const code = params.get('code');
    const error = params.get('error');

    const channel = new window.BroadcastChannel(OAUTH_CHANNEL_NAME);
    channel.postMessage({ type: OAUTH_POST_MESSAGE_TYPE, payload: { code, search, error } });
    channel.close();

    return () => {
      channel.close();
    };
  }, []);

  return <WaitingAuthentication />;
};

/** Fires analytics `page` events on SPA navigation. Mounted inside <Router>; renders nothing. */
const RouteTracker = () => {
  usePageTracking();
  return null;
};

const OAuthCallbackPage: React.FC = () => {
  const { userStore } = useStores();
  const params = useParams<{ role: string; rudderCategory?: RudderCategory }>();
  const location = useLocation();

  useEffect(() => {
    const oauthCallback = async () => {
      const isDest = params.rudderCategory === RESOURCE_TYPES.DESTINATION;
      const oauthMetaKey = isDest ? SESSION_STORAGE_KEYS.destination : SESSION_STORAGE_KEYS.source;

      const { pathname, search } = location;
      const OauthMetadata = sessionStorage.getItem(oauthMetaKey);

      let extraQueryParams = {
        t: window.btoa(userStore.token),
        workspaceId: userStore?.selectedWorkspaceId,
      };

      if (OauthMetadata) {
        try {
          const metadataFromStorage = JSON.parse(OauthMetadata);
          extraQueryParams = {
            ...metadataFromStorage,
            ...extraQueryParams,
          };
        } catch (err) {}
      }

      const urlData = await apiAuthCaller({ nonWorkspaceRoute: true }).get(`${pathname}${search}`, {
        params: extraQueryParams,
      });

      window.location.assign(urlData.data);
    };

    oauthCallback();
  }, []);

  return <WaitingAuthentication role={params.role} />;
};

/**
 * Root layout route: renders the always-present chrome (meta tags, page tracker)
 * and the app-wide Suspense boundary, with the matched route rendered through
 * <Outlet />. Mounted once at the top of the data-router tree so it persists
 * across navigation.
 *
 * APL-446 — the Suspense boundary MUST be keyed by the matched leaf route.
 * Under React 18, when a navigation fires while a lazy route chunk is still
 * loading, a shared (unkeyed) boundary stays stuck on its fallback and the
 * destination route never renders — even with startTransition-wrapped router
 * updates (verified by __tests__/lazyRouteNavigation.test.tsx). Remounting the
 * boundary per matched route makes the destination render immediately. The
 * key is the route id (stable per route pattern), so param-only navigation
 * within the same route does not remount the page.
 */
const RootLayout: React.FC = () => {
  const matches = useMatches();
  const leafRouteId = matches[matches.length - 1]?.id;
  return (
    <>
      <TopProgressBar />
      <MetaTag />
      <RouteTracker />
      <Suspense key={leafRouteId} fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </>
  );
};

const AppRouter: React.FC = () => {
  const { sourceDefinitionsListStore, destinationDefsListStore, accountDefinitionsListStore } =
    useStores();

  useEffect(() => {
    const definitionApiCalls = [
      sourceDefinitionsListStore.getAll(),
      destinationDefsListStore.getDestinationDefs(),
      accountDefinitionsListStore.getAccountDefinitions(),
    ];

    Promise.all(definitionApiCalls);
  }, []);

  // The router MUST be created exactly once per AppRouter mount (empty dep
  // array): recreating it leaks the old router's popstate/pagehide listeners
  // (RouterProvider never disposes it) and tears the rendered state. All
  // async/conditional branching lives inside stable route elements above.
  const router = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <Route element={<RootLayout />} errorElement={<RouteErrorBoundary />}>
            {/* `/*` restores v5 prefix matching so /health/live also resolves here */}
            <Route path="/health/*" element={<h3>Hey There!!! The App is Healthy</h3>} />
            <Route path="/authorize" element={<McpAuthorizationPage />} />
            <Route path="/blendoSignup" element={<BlendoSignupPage />} />
            <Route path={Path.SIGNUP} element={<SignupComponent />} />
            <Route path={Path.LOGIN} element={<LoginPage />} />
            <Route path="/esignup" element={<EsignupSwitch />} />
            <Route path="/elogin" element={<LoginPage />} />
            <Route path="/sso" element={<SSOPage />} />
            <Route path="/oauth" element={<OauthRedirect />} />
            <Route path="/auth/:role/callback" element={<OAuthCallbackPage />} />
            <Route path="/auth/:rudderCategory/:role/callback" element={<OAuthCallbackPage />} />
            <Route path="/oauth/success/:id" element={<WaitingAuthentication />} />
            <Route path="/oauth/error/:message" element={<WaitingAuthentication />} />
            <Route path="/forgotPassword" element={<ForgotPassword />} />
            <Route path="/acceptInvite" element={<AcceptInvite />} />
            {enableMFA && <Route path="/addmfa" element={<GuardedAddMfa />} />}
            {enableMFA && <Route path="/addmfalater" element={<GuardedAddMfaLater />} />}
            {enableMFA && <Route path="/addmfasuccess" element={<GuardedAddMfaSuccess />} />}
            {enableMFA && <Route path="/addphone" element={<GuardedAddPhone />} />}
            {enableMFA && <Route path="/verifyphone" element={<GuardedVerifyPhone />} />}
            {enableMFA && <Route path="/verify-totp" element={<GuardedVerifyTotp />} />}
            {enableMFA && <Route path="/setup-totp" element={<GuardedSetupTotp />} />}
            <Route path={Path.ACCOUNT_SETUP} element={<SetupAccountSwitch />} />
            <Route path={Path.USER_SUSPENDED} element={<GuardedUserSuspended />} />
            <Route path={`${Path.SELECT_ORGANIZATION}/*`} element={<GuardedSelectOrganization />} />
            <Route path="/*" element={<GuardedHome />} />
          </Route>,
        ),
        { basename: process.env.PUBLIC_URL || '/' },
      ),
    [],
  );

  return (
    <>
      <Toast />
      <Suspense fallback={null}>
        <Security />
      </Suspense>
      {/* react-router v7 wraps router state updates in React.startTransition by
          default (the old v6 `future.v7_startTransition` opt-in). Note that does
          NOT fix the APL-446 stuck-skeleton hang on its own — the keyed Suspense
          boundary in RootLayout does; see __tests__/lazyRouteNavigation.test.tsx. */}
      <RouterProvider router={router} />
    </>
  );
};

/* eslint-disable import/no-default-export */
export default observer(AppRouter);
